import { getToken } from "../../utilities/authHelper";
import { getRawTickerFromCatalogValue } from "../../utilities/marketSymbols";
import { formatCoTraderFilterLabel } from "./TickerUniverseOptions";

export const EMPTY_STOCKS_CONFIG = {
  subscriptionType: "",
  accessTier: "",
  defaultSymbol: "",
  options: {},
};

const normalizeStockOptions = (rawOptions) => {
  if (!rawOptions || typeof rawOptions !== "object" || Array.isArray(rawOptions)) {
    return {};
  }

  return Object.entries(rawOptions).reduce((countryAccumulator, [country, categories]) => {
    if (!categories || typeof categories !== "object" || Array.isArray(categories)) {
      return countryAccumulator;
    }

    const normalizedCategories = Object.entries(categories).reduce(
      (categoryAccumulator, [category, symbols]) => {
        const normalizedSymbols = (Array.isArray(symbols) ? symbols : [])
          .filter((symbol) => symbol?.value)
          .map((symbol) => ({
            ...symbol,
            value: String(symbol.value),
            label: formatCoTraderFilterLabel(symbol.label || symbol.value),
          }));

        if (normalizedSymbols.length > 0) {
          categoryAccumulator[category] = normalizedSymbols;
        }

        return categoryAccumulator;
      },
      {},
    );

    if (Object.keys(normalizedCategories).length > 0) {
      countryAccumulator[country] = normalizedCategories;
    }

    return countryAccumulator;
  }, {});
};

const getErrorMessage = (status) => {
  if (status === 401) return "User is not authenticated.";
  if (status === 403) return "No active stock entitlement is available.";
  return "Stock universe is unavailable.";
};

export const fetchCoTraderStocksConfig = async () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = getToken();

  if (!apiUrl || !token) {
    const error = new Error(getErrorMessage(token ? 500 : 401));
    error.status = token ? 500 : 401;
    throw error;
  }

  const response = await fetch(`${apiUrl}/get_stocks`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  const payload = await response.json().catch(() => null);

  if (!response.ok || payload?.success === false) {
    const error = new Error(
      payload?.errorObject?.message ||
        payload?.message ||
        getErrorMessage(response.status),
    );
    error.status = response.status;
    throw error;
  }

  const results = payload?.results || {};
  const options = normalizeStockOptions(results.options);

  return {
    subscriptionType: results.subscription_type || "",
    accessTier: results.access_tier || "",
    defaultSymbol: results.default_symbol || "",
    options,
  };
};

export const getStockSymbolsFromOptions = (stockOptions = {}) =>
  Object.values(stockOptions)
    .flatMap((categories) => Object.values(categories || {}))
    .flatMap((symbols) => (Array.isArray(symbols) ? symbols : []));

export const getStockDisplayCode = (rawSymbol) =>
  getRawTickerFromCatalogValue(rawSymbol);

export const getStockSelectionForSymbol = (stockOptions = {}, symbolValue) => {
  const preferredValue = symbolValue ? String(symbolValue) : "";

  for (const [country, categories] of Object.entries(stockOptions)) {
    for (const [category, symbols] of Object.entries(categories || {})) {
      const matchedSymbol = (Array.isArray(symbols) ? symbols : []).find(
        (symbol) => symbol.value === preferredValue,
      );

      if (matchedSymbol) {
        return {
          country: { value: country, label: formatCoTraderFilterLabel(country) },
          category: { value: category, label: formatCoTraderFilterLabel(category) },
          symbol: matchedSymbol,
        };
      }
    }
  }

  return null;
};

export const getInitialStockSelection = (stockOptions = {}, defaultSymbol = "") => {
  const storedSymbol = localStorage.getItem("default_symbol_stock");
  const preferredSelection =
    getStockSelectionForSymbol(stockOptions, defaultSymbol) ||
    getStockSelectionForSymbol(stockOptions, storedSymbol);

  if (preferredSelection) {
    return preferredSelection;
  }

  for (const [country, categories] of Object.entries(stockOptions)) {
    for (const [category, symbols] of Object.entries(categories || {})) {
      const firstSymbol = Array.isArray(symbols) ? symbols[0] : null;

      if (firstSymbol) {
        return {
          country: { value: country, label: formatCoTraderFilterLabel(country) },
          category: { value: category, label: formatCoTraderFilterLabel(category) },
          symbol: firstSymbol,
        };
      }
    }
  }

  return null;
};
